<template>
  <div class="v2-support-modal-hero">
    <!-- Step-indexed image. Both old and new layers are absolutely-positioned
         so the default-mode <transition> can crossfade them: leaving image
         fades + scales down slightly while entering image fades + scales up
         from a hair below 1, giving the swap a subtle parallax feel. -->
    <transition name="v2sm-hero-img">
      <img
        v-if="image"
        :key="image"
        :src="image"
        :alt="caption"
        class="v2-support-modal-hero__img"
        :class="`v2-support-modal-hero__img--step${step}`"
        :style="imgStyle"
        :loading="step === 1 ? 'eager' : 'lazy'"
      />
    </transition>
    <div class="v2-support-modal-hero__scrim" aria-hidden="true"></div>
    <transition name="v2sm-hero-caption" mode="out-in">
      <div class="v2-support-modal-hero__caption" :key="step">
        <span class="v2-support-modal-hero__label">Step {{ step }} of 3</span>
        <p class="v2-support-modal-hero__text">{{ caption }}</p>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * Right-column / top-banner hero for V2SupportModal. Renders a
 * step-indexed image with a dark gradient scrim so the caption
 * reads on any photo. Collapses to 200px banner on mobile via
 * parent CSS (we don't branch layout here).
 */
const STEP_IMAGES = {
  1: '/img/dig-pink-v2/fundraise-create.jpg',
  2: '/img/dig-pink-v2/fundraise-share.jpg',
  3: '/img/dig-pink-v2/fundraise-grow.jpg',
}

const STEP_CAPTIONS = {
  1: "Let's get your email so we can set up your page.",
  2: 'Tell us about your campaign and event.',
  3: "You're all set.",
}

export default {
  name: 'V2SupportModalHero',
  props: {
    step: { type: Number, required: true, validator: v => v >= 1 && v <= 3 },
    /**
     * Optional 3-slot { image, caption } override sourced from the
     * foundation's template content. Per-slot fallback: a missing or
     * empty image/caption falls back to the matching STEP_IMAGES /
     * STEP_CAPTIONS default — admins can customize one step without
     * having to fill all three.
     */
    stepsOverride: { type: Array, default: null },
  },
  computed: {
    overrideEntry() {
      if (!Array.isArray(this.stepsOverride)) return null
      return this.stepsOverride[this.step - 1] || null
    },
    image() {
      const o = this.overrideEntry?.image
      if (typeof o === 'string' && o.trim() !== '') return o
      return STEP_IMAGES[this.step]
    },
    caption() {
      const o = this.overrideEntry?.caption
      if (typeof o === 'string' && o.trim() !== '') return o
      return STEP_CAPTIONS[this.step]
    },
    /**
     * Per-step focal point as { x, y } percentages → object-position.
     * Falls back to the global default (50% 28%) baked into the CSS
     * when the admin hasn't set one (matches the default reasoning:
     * the hero photos all feature standing people with faces in the
     * upper third). Inline style overrides the CSS rule when present.
     */
    imgStyle() {
      const fp = this.overrideEntry?.focal_point
      if (fp && typeof fp.x === 'number' && typeof fp.y === 'number') {
        return { objectPosition: `${fp.x}% ${fp.y}%` }
      }
      return null
    },
  },
}
</script>

<style scoped>
.v2-support-modal-hero {
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--sideout-navy, #1D2549);
  overflow: hidden;
}
.v2-support-modal-hero__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Anchor the subject toward the upper third of the frame. The hero
     photos all feature standing people with faces in the top portion;
     the default 50% center crop hides their heads on the short mobile
     banner (200-220px tall). 28% keeps faces visible while still
     showing context below. */
  object-position: center 28%;
  transition: opacity 300ms cubic-bezier(0.4, 0, 0.2, 1);
}
/* Step 2 (the campaign-form step) hero is a portrait group photo whose four
   standing subjects sit centered in the frame (Rick 2026-06-23: "get the
   people in the middle of the container"). On the tall desktop hero column a
   center-ish crop frames them with faces in the upper-middle and full bodies
   below; the prior 68% (tuned for the old low-standing bleacher banner) now
   pushed the crop down onto their legs. Desktop only (min-width:1024px) — the
   hero column is display:none below 1024px in V2SupportModal, so there's no
   mobile band to balance against. An admin focal_point (inline style) still
   overrides this. */
@media (min-width: 1024px) {
  .v2-support-modal-hero__img--step2 { object-position: 50% 46%; }
}
.v2-support-modal-hero__scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(29, 37, 73, 0.82) 0%,
    rgba(29, 37, 73, 0.30) 55%,
    rgba(29, 37, 73, 0.12) 100%
  );
}
.v2-support-modal-hero__caption {
  position: absolute;
  left: 32px;
  right: 32px;
  bottom: 32px;
  color: #ffffff;
  font-family: 'Red Hat Display', sans-serif;
}
.v2-support-modal-hero__label {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #F0A3CE;
  margin-bottom: 8px;
}
.v2-support-modal-hero__text {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
  text-wrap: balance;
}
/* ─── Hero image crossfade (default mode = simultaneous) ─ */
/* Both <img> layers occupy the same absolute coords, so the leaving
   layer fades + scales down while the entering layer fades + scales up
   from 1.04 → 1. The 480ms duration matches the step pane slide so the
   left-column form and right-column hero land their motion together. */
.v2sm-hero-img-enter-active,
.v2sm-hero-img-leave-active {
  transition: opacity 480ms cubic-bezier(0.22, 1, 0.36, 1),
              transform 700ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: opacity, transform;
}
.v2sm-hero-img-enter-from {
  opacity: 0;
  transform: scale(1.04);
}
.v2sm-hero-img-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

/* ─── Hero caption crossfade (mode="out-in") ───────────── */
/* Caption uses out-in so only one block renders at a time — the absolute
   positioning would otherwise stack two captions and cause the eyebrow
   label to flash overlapping. */
.v2sm-hero-caption-enter-active {
  transition: opacity 320ms ease 80ms, transform 380ms cubic-bezier(0.22, 1, 0.36, 1) 80ms;
}
.v2sm-hero-caption-leave-active {
  transition: opacity 180ms ease, transform 240ms cubic-bezier(0.4, 0, 0.2, 1);
}
.v2sm-hero-caption-enter-from {
  opacity: 0;
  transform: translateY(12px);
}
.v2sm-hero-caption-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (prefers-reduced-motion: reduce) {
  .v2-support-modal-hero__img { transition-duration: 60ms; }
  .v2sm-hero-img-enter-active,
  .v2sm-hero-img-leave-active,
  .v2sm-hero-caption-enter-active,
  .v2sm-hero-caption-leave-active {
    transition-duration: 80ms !important;
    transform: none !important;
  }
  .v2sm-hero-img-enter-from,
  .v2sm-hero-img-leave-to,
  .v2sm-hero-caption-enter-from,
  .v2sm-hero-caption-leave-to {
    transform: none !important;
  }
}
</style>
